import React from 'react'
import Axios from 'axios'
import JwtDecode from 'jwt-decode'
import ListItemButton from 'components_new/atoms/List/ListItemButton'

import { reportAuthEvent } from 'utils/authEvents'

import {
  login,
  setNewPasswordChallenge,
  forgotPassword,
  confirmPassword,
  saveSamlSession,
  sendMFACode,
  CALLBACK_URL,
  COGNITO_BASE_URL
} from './AuthModule'
import { takePkceVerifier, withPkce } from './pkce'

import {
  VERIFICATION_CODE,
  SET_PASSWORD,
  SIGNED_IN,
  LOGIN,
  MFA_REQUIRED
} from './utils'
import ListItemText from 'components_new/atoms/List/ListItemText'
import ListItemIcon from 'components_new/atoms/List/ListItemIcon'
import Icon from 'components_new/atoms/Icon'

const API_URL =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:3990/'
    : process.env.REACT_APP_HOMEPAL_MDM_API_PROD

export const handleErrors = (errors, formRef) => {
  if (errors.length > 0) {
    const values = formRef.getFieldsValue()
    const parsedErrors = Object.keys(values).map((key) => {
      return {
        name: key,
        value: values[key],
        errors: errors
          .filter((e) => !e.field || e.field === key)
          .map((e) => e.message)
      }
    })

    formRef.setFields(parsedErrors)
  } else {
    formRef.resetFields()
  }
}

export const handleIdp = async (setState, setBlank) => {
  const code = window.location.search.split('?code=')[1].split('&')[0]
  const errors = []

  if (code) {
    const verifier = takePkceVerifier()

    try {
      const response = await Axios.post(
        `${COGNITO_BASE_URL}/oauth2/token?client_id=${process.env.REACT_APP_COGNITO_CLIENT_ID}&grant_type=authorization_code&code=${code}&redirect_uri=${CALLBACK_URL}${verifier ? `&code_verifier=${verifier}` : ''}`,
        {},
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      )

      try {
        await saveSamlSession(response.data)
        setState(SIGNED_IN)
        setBlank(false)
      } catch (e) {
        reportAuthEvent(
          'FEDERATED_LOGIN_FAILED',
          JwtDecode(response.data.id_token).email
        )
        errors.push({
          message: 'Du har inte tillgång till den valda organisationen.'
        })
      }

      setState(SIGNED_IN)
      setBlank(false)
    } catch (e) {
      reportAuthEvent('FEDERATED_LOGIN_FAILED')
      errors.push({ message: 'Något gick fel, var god försök igen.' })
    }
  }

  if (errors.length > 0) {
    setState(LOGIN)
    setBlank(false)
  }

  return errors
}

export const handleLoginWithAzure = async (values, setIdpSelections) => {
  const idps = (
    await Axios.get(`${API_URL}idp?email=${encodeURIComponent(values.email)}`)
  ).data.data

  const redirect = async (url) => {
    window.location.href = await withPkce(url)
  }

  if (idps.length === 1) {
    await redirect(idps[0].redirect_url)
  } else if (idps.length > 1) {
    setIdpSelections(
      idps.map((idp, i) => (
        <ListItemButton key={i} onClick={() => redirect(idp.redirect_url)}>
          <ListItemIcon>
            <Icon name="LocationCity" />
          </ListItemIcon>
          <ListItemText primary={idp.customer_name} />
          <Icon name="ArrowForward" />
        </ListItemButton>
      ))
    )
  }
}

/**
 * Sends the user on to SSO when their email domain has it. Resolves to
 * usePassword: true when the domain has no SSO, and to an error when the
 * lookup itself fails.
 */
export const handleSsoLookup = async (values, setIdpSelections) => {
  try {
    await handleLoginWithAzure(values, setIdpSelections)

    return { usePassword: false, errors: [] }
  } catch (e) {
    // GET /idp answers 404 when the domain has no SSO.
    if (e.response?.status === 404) {
      return { usePassword: true, errors: [] }
    }

    reportAuthEvent('FEDERATED_LOGIN_FAILED', values.email)

    return {
      usePassword: false,
      errors: [{ message: 'Något gick fel, var god försök igen.' }]
    }
  }
}

export const handleLogin = async (
  values,
  setState,
  setUser,
  rememberMe = false,
  redirectOnError = null
) => {
  const response = await login(
    {
      email: values.email,
      password: values.password
    },
    rememberMe
  )

  const errors = []

  if (response.type === SIGNED_IN) {
    setState(SIGNED_IN)
  } else if (response.type === SET_PASSWORD) {
    setState(SET_PASSWORD)
    setUser(response.user)
  } else if (response.type === MFA_REQUIRED) {
    setState(MFA_REQUIRED)
    setUser(response.user)
  } else if (response.type === 'ERROR') {
    reportAuthEvent('LOGIN_FAILED', values.email)
    errors.push({ message: response.error })

    if (redirectOnError) {
      redirectOnError()
    }
  } else if (response.type === 'INVALID_EMAIL') {
    reportAuthEvent('LOGIN_FAILED', values.email)
    errors.push({ message: response.error, field: 'email' })

    if (redirectOnError) {
      redirectOnError()
    }
  }

  return errors
}

export const handleSetPassword = async (values, setState, user, email) => {
  const errors = []

  try {
    const clientMetadata =
      values.first_name && values.last_name
        ? {
            first_name: values.first_name,
            last_name: values.last_name
          }
        : undefined

    await setNewPasswordChallenge(values.password, user, clientMetadata)

    reportAuthEvent('FIRST_LOGIN', email)
    setState(SIGNED_IN)
  } catch (e) {
    reportAuthEvent('SET_PASSWORD_FAILED', email)
    errors.push({ message: 'Något gick fel...' })
  }

  // Don't wanna throw error to user if logging fails.
  if (errors.length === 0) {
    logInviteAccepted(email)
  }

  return errors
}

export const handleForgotPassword = async (values, setPasswordHasBeenReset) => {
  const response = await forgotPassword(values.email)
  const errors = []

  if (response.type === VERIFICATION_CODE) {
    setPasswordHasBeenReset(true)
  } else if (response.type === 'ERROR') {
    errors.push({ message: response.error })
  }

  return errors
}

export const handleVerificationCode = async (values, setState, user) => {
  const response = await confirmPassword(values.code, values.password, user)
  const errors = []

  if (response.type === SIGNED_IN) {
    setState(SIGNED_IN)
  } else if (response.type === 'ERROR') {
    reportAuthEvent('VERIFICATION_CODE_FAILED', user.username)
    errors.push({ field: 'code', message: response.error })
  }

  return errors
}

const logInviteAccepted = async (email) => {
  try {
    await Axios.patch(
      `${API_URL}v1/public/measurements/invite-accepted-log/${email}`
    )
  } catch (e) {}
}

export const handleMFA = async (values, setState, user, rememberMe = false) => {
  const response = await sendMFACode(user, values.code, rememberMe)
  const errors = []

  if (response.type === SIGNED_IN) {
    setState(SIGNED_IN)
  } else if (response.type === 'ERROR') {
    reportAuthEvent('MFA_FAILED', user.username)
    errors.push({ message: response.error })
  }

  return errors
}
